import { ChevronDown16 } from '@carbon/icons-react';
import { Box } from '@primer/react';
import { NavLinkType } from '@upright/ui/common/generic/content/texts/bou-link/types';
import { AboutUprightModal } from '@upright/ui/common/generic/modals/about-upright-modal';
import { KBUrl } from '@upright/ui/common/upright/kb-urls';
import { loggedInUser } from '@upright/ui/untidy/redux/selectors';
import { useMainContext } from '@upright/ui/views/root/main/context';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Heading } from '../../../../primer-custom/heading';
import {
  Arrow,
  ContentList,
  ListItem,
  ListItemInner,
  MenuContent,
  MenuItemTriggerWrapper,
  MenuList,
  MenuRoot,
  MenuTrigger,
  MenuViewport,
  StyledIndicator,
} from '../shared';
import { solutionsMenuGroups } from './data';
import { MenuLink } from './radix-nav-link';

const MenuActionButton = styled.button`
  all: unset;
  cursor: pointer;
  width: 100%;
`;

/* "Solutions" dropdown, mirrored from upright-facade's
   `src/facade/src/new-lander-page/nav.tsx` (`SOLUTIONS_COLUMNS`).
   The platform header keeps its existing dark Primer/Radix dropdown style,
   so we render the four audience groups (For investors / For companies /
   For advisors / For universities) as vertically stacked sections inside
   the existing narrow MenuContent — separated by horizontal dividers, the
   same pattern the previous Products menu used for its Use cases / Data
   sections. The audience heading is itself a link in the facade design, so
   we wrap the heading in a MenuLink too. */
export const NavMenu = () => {
  const user = useSelector(loggedInUser);
  const [, setContactModalOpen] = useMainContext().contactModal;
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

  return (
    <>
      <MenuRoot delayDuration={0}>
        <MenuList>
          <MenuItemTriggerWrapper>
            <MenuTrigger>
              <span>Solutions</span>
              <ChevronDown16 />
            </MenuTrigger>
            <MenuContent>
              {solutionsMenuGroups.map((group, gi) => (
                <Box key={group.heading.linkText}>
                  {gi > 0 && <Box height={1} backgroundColor="border.dark" my={2} mx={3} />}
                  <Box py={2} px={3}>
                    <MenuLink to={group.heading.url} linkType={NavLinkType.EXTERNAL}>
                      <Heading as="h6" fontSize="micro" sx={{ color: 'fg.onEmphasis' }}>
                        {group.heading.linkText}
                      </Heading>
                    </MenuLink>
                  </Box>
                  {group.items.length > 0 && (
                    <ContentList>
                      {group.items.map(item => (
                        <ListItem key={item.url}>
                          <MenuLink to={item.url} linkType={NavLinkType.EXTERNAL}>
                            <ListItemInner>{item.linkText}</ListItemInner>
                          </MenuLink>
                        </ListItem>
                      ))}
                    </ContentList>
                  )}
                </Box>
              ))}
            </MenuContent>
          </MenuItemTriggerWrapper>
          <MenuItemTriggerWrapper>
            <MenuTrigger>
              <span>About</span>
              <ChevronDown16 />
            </MenuTrigger>
            <MenuContent>
              <ContentList>
                <ListItem>
                  <MenuLink to="https://www.uprightproject.com/model/" linkType={NavLinkType.EXTERNAL}>
                    <ListItemInner>Model</ListItemInner>
                  </MenuLink>
                </ListItem>
                <ListItem>
                  <MenuLink to="https://www.uprightproject.com/mission/" linkType={NavLinkType.EXTERNAL}>
                    <ListItemInner>Mission</ListItemInner>
                  </MenuLink>
                </ListItem>
                <ListItem>
                  <MenuLink to={KBUrl.INDEX} linkType={NavLinkType.EXTERNAL}>
                    <ListItemInner>Knowledge base</ListItemInner>
                  </MenuLink>
                </ListItem>
                <ListItem>
                  <MenuActionButton
                    onClick={() => {
                      setIsAboutModalOpen(true);
                    }}
                  >
                    <ListItemInner>About Upright Platform</ListItemInner>
                  </MenuActionButton>
                </ListItem>
                {!user && (
                  <ListItem>
                    <MenuActionButton
                      onClick={() => {
                        setContactModalOpen(true);
                      }}
                    >
                      <ListItemInner>Contact</ListItemInner>
                    </MenuActionButton>
                  </ListItem>
                )}
              </ContentList>
            </MenuContent>
          </MenuItemTriggerWrapper>
          <StyledIndicator>
            <Arrow />
          </StyledIndicator>
        </MenuList>
        <MenuViewport />
      </MenuRoot>
      <AboutUprightModal
        isOpen={isAboutModalOpen}
        setIsOpen={setIsAboutModalOpen}
        onContactLinkPress={() => {
          setIsAboutModalOpen(false);
          setContactModalOpen(true);
        }}
      />
    </>
  );
};
